import React, { useState, useEffect } from 'react';
import { Container, Grid, Text, Title, Stack, Image, Paper, ThemeIcon } from '@mantine/core';
import { Helmet } from 'react-helmet-async';
import { Loader } from '../../components/common/Loader';
import { IconTarget, IconEye, IconHeart, IconLeaf } from '@tabler/icons-react';
import { useAppSelector } from '../../store/hooks';
import { SectionTitle } from '../../components/common/SectionTitle';
import { useSEO } from '../../hooks/useSEO';
import { galleryService } from '../../services/galleryService';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

const valueIcons: Record<string, any> = {
  quality: IconHeart,
  integrity: IconTarget,
  customer: IconEye,
  sustainability: IconLeaf,
};

export const AboutPage: React.FC = () => {
  const { content } = useAppSelector((state) => state.content);
  const about = content.pages.about;
  const { seo, loading: seoLoading } = useSEO('about');
  const [companyImage, setCompanyImage] = useState(about.image);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    galleryService.getPublicGallery('ABOUT_US')
      .then(gallery => {
        const img = gallery.items.find(item => item.type === 'IMAGE' && item.active);
        if (img && img.imageUrl) {
          // Construct full URL if relative
          const fullUrl = img.imageUrl.startsWith('http')
            ? img.imageUrl
            : `${API_URL}${img.imageUrl.startsWith('/') ? '' : '/'}${img.imageUrl}`;
          setCompanyImage(fullUrl);
        }
      })
      .catch(err => console.error('Failed to fetch about gallery:', err))
      .finally(() => setImageLoading(false));
  }, []);

  if (seoLoading || imageLoading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader />
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seo?.metaTitle}</title>
        <meta name="description" content={seo?.metaDescription} />
        <meta name="keywords" content={seo?.metaKeywords?.join(', ')} />
        <meta property="og:title" content={seo?.ogTitle} />
        <meta property="og:description" content={seo?.ogDescription} />
        {seo?.ogImage && <meta property="og:image" content={seo.ogImage} />}
        <meta name="twitter:title" content={seo?.twitterTitle} />
        <meta name="twitter:description" content={seo?.twitterDescription} />
        {seo?.twitterImage && <meta name="twitter:image" content={seo.twitterImage} />}
        {seo?.canonicalUrl && <link rel="canonical" href={seo.canonicalUrl} />}
      </Helmet>

      <Container size="xl">
        <SectionTitle title={about.title} />

        <Grid gutter={40}>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="lg">
              <Text size="lg" style={{ lineHeight: 1.8 }}>
                {about.description}
              </Text>

              <Paper p="xl" withBorder radius="lg">
                <Title order={3} mb="md">Our Mission</Title>
                <Text>{about.mission}</Text>
              </Paper>

              <Paper p="xl" withBorder radius="lg">
                <Title order={3} mb="md">Our Vision</Title>
                <Text>{about.vision}</Text>
              </Paper>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Image
              src={companyImage}
              alt="About Thalvora Exports"
              radius="lg"
              style={{ objectFit: 'cover', height: 400 }}
              onError={(e) => {
                // Fallback to placeholder if dynamic image fails
                e.currentTarget.src = about.image;
              }}
              loading="lazy" 
            />
          </Grid.Col>
        </Grid>

        <SectionTitle title="Our Core Values" />

        <Grid>
          {about.coreValues.map((value) => {
            const IconComponent = valueIcons[value.icon] || IconHeart;
            
            return (
              <Grid.Col key={value.title} span={{ base: 12, sm: 6, md: 3 }}>
                <Paper p="xl" withBorder radius="lg">
                  <Stack align="center" gap="md">
                    <ThemeIcon size={60} radius="xl" color="primary" variant="light">
                      <IconComponent size={30} />
                    </ThemeIcon>
                    <Title order={3} ta="center">{value.title}</Title>
                    <Text ta="center" c="dimmed">{value.description}</Text>
                  </Stack>
                </Paper>
              </Grid.Col>
            );
          })}
        </Grid>
      </Container>
    </>
  );
};